// Which displayed classes sit more than one level below a displayed root.
//
// Hiding "subclasses" cannot mean "every class that has a parent": a core class
// and its immediate subclasses are the level that gives the core class meaning,
// and on a store whose own classes are mostly roots that rule hid one node in
// eight (list 27.28). Depth is read from the subClassOf edges ON THE CANVAS, so
// a class whose parent is not displayed reads as a root, and a subclass pulled
// in by Expand is measured the same way as one drawn by Load Ontology.
export interface SubClassOfEdge {
  child: string;
  parent: string;
}

export function classesBelowFirstLevel(
  nodeIds: Iterable<string>,
  edges: Iterable<SubClassOfEdge>,
): Set<string> {
  const displayed = new Set(nodeIds);
  const parents = new Map<string, string[]>();
  for (const e of edges) {
    if (e.child === e.parent) continue;
    if (!displayed.has(e.child) || !displayed.has(e.parent)) continue;
    const known = parents.get(e.child);
    if (known) known.push(e.parent);
    else parents.set(e.child, [e.parent]);
  }

  const depth = new Map<string, number>();
  const chain = new Set<string>();
  // Shallowest ancestry wins: a class with two parents is as deep as its
  // nearest root. A subClassOf cycle — real ontologies carry them — terminates
  // by treating the edge back into the current chain as absent.
  const depthOf = (id: string): number => {
    const settled = depth.get(id);
    if (settled !== undefined) return settled;
    if (chain.has(id)) return Infinity;
    chain.add(id);
    let nearest = Infinity;
    for (const parent of parents.get(id) ?? []) {
      const d = depthOf(parent) + 1;
      if (d < nearest) nearest = d;
    }
    chain.delete(id);
    const result = nearest === Infinity ? 0 : nearest;
    depth.set(id, result);
    return result;
  };

  const deeper = new Set<string>();
  for (const id of displayed) {
    if (depthOf(id) >= 2) deeper.add(id);
  }
  return deeper;
}
